// Isometric 3D box. Depth is deliberately exaggerated way past real-world
// proportions (~2.35m wide vs up to 13.55m long) — at true-to-life ratios the
// top/side faces become razor-thin slivers and the box reads as broken/
// incomplete at icon size. Chunky, non-realistic depth is what makes small
// isometric icons read as solid — standard icon-design practice.
//
// ISO_DEPTH must stay <= 16 (verified for every L/H combo below): above that,
// the front wall's bottom edge and the back wall's top edge stop overlapping
// in screen space, leaving a real gap where the roof face (drawn under
// everything) shows through as a stray triangle — not an anti-aliasing
// artifact, an actual uncovered region between two non-adjacent 3D edges
// that happen to project near each other at shallower depths.
const ISO_CANVAS = { w: 50, h: 52 };
const ISO_DEPTH = 14;
const ISO_MAX_H = 21;
const ISO_OFFSET = { x: 16, y: 23 };

function isoPoint(a, b, c) {
  const x = (a - b) * 0.866 + ISO_OFFSET.x;
  const y = (a + b) * 0.5 - c + ISO_OFFSET.y;
  return `${x.toFixed(1)},${y.toFixed(1)}`;
}

export function ContainerIcon({ lengthFt, highCube }) {
  const L = lengthFt <= 20 ? 22 : lengthFt <= 40 ? 34 : 38;
  const H = highCube ? ISO_MAX_H : 16;

  const top   = [isoPoint(0, 0, H), isoPoint(L, 0, H), isoPoint(L, ISO_DEPTH, H), isoPoint(0, ISO_DEPTH, H)].join(" ");
  const front = [isoPoint(0, 0, 0), isoPoint(L, 0, 0), isoPoint(L, 0, H), isoPoint(0, 0, H)].join(" ");
  const side  = [isoPoint(L, 0, 0), isoPoint(L, ISO_DEPTH, 0), isoPoint(L, ISO_DEPTH, H), isoPoint(L, 0, H)].join(" ");
  // Left-end and back walls — the two faces not otherwise drawn. Without
  // them, the roof's back corners have nothing beneath them and the top
  // face's own color shows through as disconnected slivers.
  const leftEnd = [isoPoint(0, 0, 0), isoPoint(0, ISO_DEPTH, 0), isoPoint(0, ISO_DEPTH, H), isoPoint(0, 0, H)].join(" ");
  const back = [isoPoint(0, ISO_DEPTH, 0), isoPoint(L, ISO_DEPTH, 0), isoPoint(L, ISO_DEPTH, H), isoPoint(0, ISO_DEPTH, H)].join(" ");
  // Ridge seam — leftEnd's and side's back-top corners (D and C), the real
  // edge where the hidden back wall meets the roof. Same color as back.
  const [ridgeX1, ridgeY1] = isoPoint(0, ISO_DEPTH, H).split(",");
  const [ridgeX2, ridgeY2] = isoPoint(L, ISO_DEPTH, H).split(",");

  // Each of the 4 walls gets its own shade — same flat color on all of them
  // made back/leftEnd/side merge into one undifferentiated dark blob with no
  // visible face boundaries, so the box read as "bright triangle on a blob"
  // instead of a box. Distinct shades per face is what makes all 5 faces
  // (and therefore the box shape itself) actually legible at icon size.
  return (
    <svg width={ISO_CANVAS.w} height={ISO_CANVAS.h} viewBox={`0 0 ${ISO_CANVAS.w} ${ISO_CANVAS.h}`} className="shrink-0">
      <polygon points={top} fill="#7dd3fc" stroke="#7dd3fc" strokeWidth="0.75" strokeLinejoin="round" />
      <polygon points={back} fill="#124a8f" stroke="#124a8f" strokeWidth="0.75" strokeLinejoin="round" />
      <polygon points={leftEnd} fill="#0e59b8" stroke="#0e59b8" strokeWidth="0.75" strokeLinejoin="round" />
      <polygon points={front} fill="#0d7ff2" stroke="#0d7ff2" strokeWidth="0.75" strokeLinejoin="round" />
      <polygon points={side} fill="#0b4fa8" stroke="#0b4fa8" strokeWidth="0.75" strokeLinejoin="round" />
      <line x1={ridgeX1} y1={ridgeY1} x2={ridgeX2} y2={ridgeY2} stroke="#124a8f" strokeWidth="0.75" />
    </svg>
  );
}
